import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, authHeaders, getToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { PasswordInput } from "@/components/PasswordInput";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { fileToDataUrl } from "@/lib/image";
import { User, LogOut, Store, ChevronRight, Loader2, Save, UserPlus, Award, Camera, Bell, KeyRound, Users, Mail, Star, CornerDownRight, Flag, Copy, Gift, Activity, PiggyBank, BellPlus, FileText } from "lucide-react";
import { toast } from "sonner";
import { DeleteAccountSection } from "@/components/DeleteAccountSection";
import PushToggle from "@/components/PushToggle";
import AlertMeSheet from "@/components/AlertMeSheet";
import { COUNTRIES, BIRTH_YEARS, GENDER_OPTIONS } from "@/lib/countries";

const INVITE_TIERS = [
  { min: 10, label: "Super Ambassador", color: "from-amber-400 to-orange-500" },
  { min: 5, label: "Ambassador", color: "from-[#E63946] to-[#ff6b6b]" },
  { min: 1, label: "Esploratore", color: "from-sky-400 to-indigo-500" },
];
function inviteTier(n) {
  const t = INVITE_TIERS.find((x) => n >= x.min);
  const next = [...INVITE_TIERS].reverse().find((x) => n < x.min);
  return { current: t, next };
}

// Single top-level menu row (subito.it style): icon box + label + chevron, expands to submenu.
const MenuItem = ({ value, icon: Icon, label, hint, testId, children }) => (
  <AccordionItem value={value} className="border-b border-slate-100 last:border-b-0">
    <AccordionTrigger data-testid={testId} className="px-4 py-4 hover:no-underline">
      <div className="flex items-center gap-3 min-w-0">
        <div className="h-10 w-10 rounded-xl bg-[#FFF1F2] flex items-center justify-center shrink-0">
          <Icon className="h-5 w-5 text-[#E63946]" />
        </div>
        <div className="min-w-0 text-left">
          <p className="font-bold text-slate-800 truncate">{label}</p>
          {hint && <p className="text-xs text-slate-400 truncate">{hint}</p>}
        </div>
      </div>
    </AccordionTrigger>
    <AccordionContent className="px-4">{children}</AccordionContent>
  </AccordionItem>
);

export default function Account() {
  const navigate = useNavigate();
  const { client, clientLogout, refreshClient } = useAuth();
  const [form, setForm] = useState({ name: "", phone: "", city: "", photo: "", country: "Italia", birth_date: "", gender: "" });
  const [notif, setNotif] = useState({ nearby: true, following: true, email: true, whatsapp: false });
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [changingPw, setChangingPw] = useState(false);
  const [emailForm, setEmailForm] = useState({ current_password: "", new_email: "" });
  const [changingEmail, setChangingEmail] = useState(false);
  const [following, setFollowing] = useState([]);
  const [saving, setSaving] = useState(false);
  const [inviteCount, setInviteCount] = useState(0);
  const [joinedCount, setJoinedCount] = useState(0);
  const [myReviews, setMyReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [alertOpen, setAlertOpen] = useState(false);
  const [macros, setMacros] = useState([]);
  const [code, setCode] = useState("");

  useEffect(() => {
    if (client) {
      setForm({ name: client.name || "", phone: client.phone || "", city: client.city || "", photo: client.photo || "", country: client.country || "Italia", birth_date: client.birth_date || "", gender: client.gender || "" });
      if (client.notif) setNotif({ nearby: true, following: true, email: true, whatsapp: false, ...client.notif });
      if (client.public_code) setCode(client.public_code);
    }
    if (getToken("client")) {
      api.get("/client/following", { headers: authHeaders("client") }).then((r) => setFollowing(r.data.stores || [])).catch(() => {});
      api.get("/client/auth/me", { headers: authHeaders("client") }).then((r) => { setInviteCount(r.data.invite_count || 0); setJoinedCount(r.data.invited_joined || 0); if (r.data.client?.public_code) setCode(r.data.client.public_code); }).catch(() => {});
      api.get("/client/reviews", { headers: authHeaders("client") }).then((r) => setMyReviews(r.data.reviews || [])).catch(() => {});
      api.get("/client/stats", { headers: authHeaders("client") }).then((r) => setStats(r.data)).catch(() => {});
      api.get("/categories").then((r) => setMacros(r.data || [])).catch(() => {});
    }
  }, [client]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, notif, birth_date: form.birth_date || "" };
      await api.put("/client/auth/profile", payload, { headers: authHeaders("client") });
      await refreshClient();
      toast.success("Profilo aggiornato");
    } catch (_) { toast.error("Errore nel salvataggio"); }
    finally { setSaving(false); }
  };

  const respondDispute = async (reviewId, action) => {
    if (action === "remove" && !window.confirm("Vuoi rimuovere questa recensione?")) return;
    try {
      await api.post(`/client/reviews/${reviewId}/dispute-respond`, { action }, { headers: authHeaders("client") });
      toast.success(action === "keep" ? "Recensione confermata" : "Recensione rimossa");
      const r = await api.get("/client/reviews", { headers: authHeaders("client") });
      setMyReviews(r.data.reviews || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const pickAvatar = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const p = await fileToDataUrl(f, 400);
    setForm((s) => ({ ...s, photo: p }));
  };

  const changePassword = async () => {
    if (!pw.current_password || !pw.new_password) return toast.error("Compila entrambi i campi");
    setChangingPw(true);
    try {
      await api.put("/client/auth/password", pw, { headers: authHeaders("client") });
      toast.success("Password aggiornata");
      setPw({ current_password: "", new_password: "" });
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
    finally { setChangingPw(false); }
  };

  const changeEmail = async () => {
    if (!emailForm.current_password || !emailForm.new_email) return toast.error("Compila entrambi i campi");
    setChangingEmail(true);
    try {
      await api.put("/client/auth/email", { current_password: emailForm.current_password.trim(), new_email: emailForm.new_email.trim().toLowerCase() }, { headers: authHeaders("client") });
      toast.success("Email di accesso aggiornata");
      setEmailForm({ current_password: "", new_email: "" });
      await refreshClient();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
    finally { setChangingEmail(false); }
  };

  const copyCode = () => {
    if (!code) return;
    try { navigator.clipboard?.writeText(code); toast.success("Codice copiato"); } catch (_) {}
  };

  const inviteLink = `${window.location.origin}/?ref=${code || client?.ref || ""}`;
  const inviteFriendsWA = () => {
    const msg = `Ciao! Ti consiglio KoupoNow, l'app dei coupon dei negozi di zona. Iscriviti qui: ${inviteLink}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  };
  const inviteFriendsEmail = () => {
    const subject = "Ti invito su KoupoNow";
    const body = `Ciao!%0A%0ATi consiglio KoupoNow, l'app dei coupon dei negozi vicino a te.%0AIscriviti qui: ${inviteLink}`;
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${body}`;
  };

  const logout = () => { clientLogout(); navigate("/"); toast.success("Disconnesso"); };

  if (!client) return (
    <div className="py-16 text-center kn-fade-up" data-testid="account-login-required">
      <div className="h-16 w-16 rounded-3xl bg-[#FFE4E6] flex items-center justify-center mx-auto mb-3"><User className="h-7 w-7 text-[#E63946]" /></div>
      <p className="font-bold text-slate-800 text-lg">Accedi al tuo profilo</p>
      <button onClick={() => navigate("/")} className="mt-4 h-11 px-5 rounded-xl kn-gradient text-white font-bold">Torna alle offerte</button>
      <div className="mt-3">
        <button onClick={() => window.dispatchEvent(new Event("kn-replay-tutorial"))} data-testid="replay-tutorial-guest" className="text-[#E63946] font-bold text-sm">Rivedi il tutorial</button>
      </div>
    </div>
  );

  const { current, next } = inviteTier(inviteCount);
  const toNext = next ? next.min - inviteCount : 0;

  return (
    <div className="space-y-4 kn-fade-up pb-6">
      {/* Profile header */}
      <div className="kn-card p-4 flex items-center gap-4" data-testid="account-header">
        <div className="h-16 w-16 rounded-2xl kn-gradient flex items-center justify-center text-white overflow-hidden shrink-0">
          {client.photo ? <img src={client.photo} alt="" className="h-full w-full object-cover" /> : <User className="h-8 w-8" />}
        </div>
        <div className="min-w-0 flex-1">
          <h1 className="text-lg font-extrabold text-slate-900 truncate">{client.name || "Il mio profilo"}</h1>
          <p className="text-slate-500 text-sm truncate">{client.email}</p>
          {code && (
            <button onClick={copyCode} data-testid="account-code" className="mt-1.5 inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full bg-[#FFE4E6] text-[#BE123C]">
              Codice: {code} <Copy className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>

      {/* Savings window (always at top) */}
      <div className="kn-card p-4" data-testid="client-savings">
        <p className="font-bold text-slate-700 flex items-center gap-2 mb-3"><PiggyBank className="h-4 w-4 text-[#E63946]" /> I tuoi risparmi</p>
        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="rounded-xl bg-emerald-50 p-3">
            <p className="text-lg font-extrabold text-emerald-700" data-testid="stat-saved">€{(stats?.total_saved || 0).toFixed(2)}</p>
            <p className="text-[11px] text-slate-500 font-semibold">Risparmiato</p>
          </div>
          <div className="rounded-xl bg-slate-50 p-3">
            <p className="text-lg font-extrabold text-slate-800" data-testid="stat-spent">€{(stats?.total_spent || 0).toFixed(2)}</p>
            <p className="text-[11px] text-slate-500 font-semibold">Speso</p>
          </div>
          <div className="rounded-xl bg-[#FFF1F2] p-3">
            <p className="text-lg font-extrabold text-[#E63946]" data-testid="stat-redeemed">{stats?.redeemed_count || 0}</p>
            <p className="text-[11px] text-slate-500 font-semibold">Coupon usati</p>
          </div>
        </div>
      </div>

      {/* Menu (categories with submenus) */}
      <div className="kn-card overflow-hidden" data-testid="account-menu">
        <Accordion type="single" collapsible className="w-full">
          {/* Profilo e dati personali */}
          <MenuItem value="profile" icon={User} label="Profilo e dati personali" hint="Foto, nome, telefono, città" testId="menu-profile">
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <label data-testid="account-avatar-upload" className="h-16 w-16 rounded-2xl bg-slate-100 border border-slate-200 flex items-center justify-center overflow-hidden cursor-pointer shrink-0">
                  {form.photo ? <img src={form.photo} alt="" className="h-full w-full object-cover" /> : <Camera className="h-5 w-5 text-slate-400" />}
                  <input type="file" accept="image/*" className="hidden" onChange={pickAvatar} />
                </label>
                <span className="text-sm text-slate-500">Tocca per cambiare la foto profilo</span>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ac-name">Nome</Label>
                <Input id="ac-name" data-testid="account-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="ac-phone">Telefono</Label>
                  <Input id="ac-phone" data-testid="account-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+39..." />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="ac-city">Città</Label>
                  <Input id="ac-city" data-testid="account-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Pesaro" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="ac-country">Nazione</Label>
                  <select id="ac-country" data-testid="account-country" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })}
                    className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#E63946]/30">
                    {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="ac-birthdate">Data di nascita</Label>
                  <input type="date" id="ac-birthdate" data-testid="account-birthdate" value={form.birth_date} onChange={(e) => setForm({ ...form, birth_date: e.target.value })}
                    max={new Date().toISOString().slice(0, 10)} min="1900-01-01"
                    className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#E63946]/30" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ac-gender">Genere</Label>
                <select id="ac-gender" data-testid="account-gender" value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })}
                  className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#E63946]/30">
                  <option value="">—</option>
                  {GENDER_OPTIONS.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
                </select>
              </div>
              <Button onClick={save} disabled={saving} data-testid="account-save" className="w-full h-11 rounded-xl font-bold gap-2">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salva
              </Button>
            </div>
          </MenuItem>

          {/* Accesso e sicurezza */}
          <MenuItem value="security" icon={KeyRound} label="Accesso e sicurezza" hint="Email di accesso e password" testId="menu-security">
            <div className="space-y-3">
              <div className="rounded-xl border border-slate-100 p-3 space-y-2.5" data-testid="email-card">
                <p className="font-bold text-slate-700 text-sm flex items-center gap-2"><Mail className="h-4 w-4" /> Modifica email di accesso</p>
                <p className="text-xs text-slate-500">Email attuale: <b className="text-slate-700">{client.email}</b></p>
                <Input type="email" data-testid="account-new-email" autoComplete="off" value={emailForm.new_email} onChange={(e) => setEmailForm({ ...emailForm, new_email: e.target.value })} placeholder="Nuova email" className="h-11 rounded-xl" />
                <PasswordInput testId="account-email-pw" autoComplete="current-password" value={emailForm.current_password} onChange={(e) => setEmailForm({ ...emailForm, current_password: e.target.value })} placeholder="Password attuale (conferma)" className="h-11 rounded-xl" />
                <Button variant="outline" onClick={changeEmail} disabled={changingEmail} data-testid="account-change-email" className="w-full h-11 rounded-xl font-bold">
                  {changingEmail ? <Loader2 className="h-4 w-4 animate-spin" /> : "Aggiorna email"}
                </Button>
              </div>
              <div className="rounded-xl border border-slate-100 p-3 space-y-2.5" data-testid="password-card">
                <p className="font-bold text-slate-700 text-sm flex items-center gap-2"><KeyRound className="h-4 w-4" /> Cambia password</p>
                <PasswordInput testId="account-current-pw" autoComplete="current-password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} placeholder="Password attuale" className="h-11 rounded-xl" />
                <PasswordInput testId="account-new-pw" autoComplete="new-password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} placeholder="Nuova password" className="h-11 rounded-xl" />
                <Button variant="outline" onClick={changePassword} disabled={changingPw} data-testid="account-change-pw" className="w-full h-11 rounded-xl font-bold">
                  {changingPw ? <Loader2 className="h-4 w-4 animate-spin" /> : "Aggiorna password"}
                </Button>
              </div>
            </div>
          </MenuItem>

          {/* Notifiche e avvisi */}
          <MenuItem value="notif" icon={Bell} label="Notifiche e avvisi" hint="Push, preferenze e avvisi personalizzati" testId="menu-notif">
            <div className="space-y-3">
              <PushToggle role="client" />
              <div className="rounded-xl border border-slate-100 p-3 space-y-2.5">
                <p className="font-bold text-slate-700 text-sm flex items-center gap-2"><Bell className="h-4 w-4" /> Preferenze</p>
                {[["nearby", "Offerte vicino a me"], ["following", "Novità dei negozi che seguo"], ["email", "Email promozionali"], ["whatsapp", "Messaggi WhatsApp"]].map(([k, label]) => (
                  <label key={k} className="flex items-center justify-between text-sm text-slate-600">
                    {label}
                    <Switch checked={!!notif[k]} onCheckedChange={(v) => setNotif((s) => ({ ...s, [k]: v }))} data-testid={`notif-${k}`} />
                  </label>
                ))}
                <Button onClick={save} disabled={saving} variant="outline" data-testid="notif-save" className="w-full h-10 rounded-xl font-bold gap-2">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salva preferenze
                </Button>
              </div>
              <button data-testid="account-alertme" onClick={() => setAlertOpen(true)}
                className="w-full rounded-2xl p-3.5 flex items-center gap-3 text-left kn-gradient text-white shadow-md hover:opacity-95 transition-opacity">
                <div className="h-11 w-11 rounded-xl bg-white/20 flex items-center justify-center shrink-0"><BellPlus className="h-5 w-5 text-white" /></div>
                <div className="min-w-0 flex-1">
                  <p className="font-extrabold text-sm flex items-center gap-2">Avvisami quando…
                    <span className="text-[10px] font-bold uppercase tracking-wide bg-white/25 rounded-full px-2 py-0.5">Consigliato</span>
                  </p>
                  <p className="text-[11px] text-white/90 mt-0.5">Categorie, zona, distanza e sconto: ti avvisiamo appena esce l'offerta giusta.</p>
                </div>
                <ChevronRight className="h-5 w-5 text-white/80" />
              </button>
            </div>
          </MenuItem>

          {/* La mia attività */}
          <MenuItem value="activity" icon={Activity} label="La mia attività" hint={`${following.length} negozi · ${myReviews.length} recensioni`} testId="menu-activity">
            <Accordion type="single" collapsible className="w-full border border-slate-100 rounded-xl overflow-hidden">
              <AccordionItem value="following" className="border-b border-slate-100">
                <AccordionTrigger data-testid="submenu-following" className="px-3 py-3 hover:no-underline text-sm">
                  <span className="flex items-center gap-2 font-semibold text-slate-700"><Store className="h-4 w-4 text-slate-400" /> Negozi che segui ({following.length})</span>
                </AccordionTrigger>
                <AccordionContent className="px-3">
                  {following.length === 0 ? (
                    <p className="text-center text-slate-400 text-sm py-3">Non segui ancora nessun negozio.</p>
                  ) : (
                    <div className="space-y-2">
                      {following.map((s) => (
                        <button key={s.vendor_id} onClick={() => navigate(`/store/${s.vendor_id}`)} className="w-full rounded-xl bg-slate-50 p-3 flex items-center gap-3 text-left">
                          <div className="h-10 w-10 rounded-xl bg-white overflow-hidden flex items-center justify-center shrink-0">
                            {s.logo ? <img src={s.logo} alt="" className="h-full w-full object-cover" /> : <Store className="h-5 w-5 text-slate-400" />}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="font-semibold text-slate-800 truncate">{s.store_name}</p>
                            <p className="text-xs text-slate-400">{s.active_count} offerte attive</p>
                          </div>
                          <ChevronRight className="h-5 w-5 text-slate-300" />
                        </button>
                      ))}
                    </div>
                  )}
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="reviews" className="border-b-0">
                <AccordionTrigger data-testid="submenu-reviews" className="px-3 py-3 hover:no-underline text-sm">
                  <span className="flex items-center gap-2 font-semibold text-slate-700"><Star className="h-4 w-4 text-slate-400" /> Le mie recensioni ({myReviews.length})</span>
                </AccordionTrigger>
                <AccordionContent className="px-3">
                  {myReviews.length === 0 ? (
                    <p className="text-center text-slate-400 text-sm py-3" data-testid="my-reviews-empty">Non hai ancora lasciato recensioni.</p>
                  ) : (
                    <div className="space-y-2" data-testid="my-reviews-list">
                      {myReviews.map((r) => (
                        <div key={r.review_id} className="rounded-xl bg-slate-50 p-3" data-testid={`my-review-${r.review_id}`}>
                          <button onClick={() => navigate(`/store/${r.vendor_id}`)} className="w-full flex items-center gap-3 text-left">
                            <div className="h-10 w-10 rounded-xl bg-white overflow-hidden flex items-center justify-center shrink-0">
                              {r.logo ? <img src={r.logo} alt="" className="h-full w-full object-cover" /> : <Store className="h-5 w-5 text-slate-400" />}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="font-semibold text-slate-800 truncate">{r.store_name || "Negozio"}</p>
                              <div className="flex items-center gap-0.5">
                                {[...Array(5)].map((_, s) => <Star key={s} className={`h-3.5 w-3.5 ${s < r.rating ? "fill-amber-400 text-amber-400" : "text-slate-200"}`} />)}
                              </div>
                            </div>
                            {r.disputed && <span className="text-[11px] font-bold text-amber-600 flex items-center gap-1 shrink-0"><Flag className="h-3 w-3" /> In revisione</span>}
                          </button>
                          {r.comment && <p className="text-sm text-slate-600 mt-2">{r.comment}</p>}
                          {r.reply && (
                            <div className="mt-2 ml-3 pl-3 border-l-2 border-[#E63946]/30 bg-white rounded-r-lg p-2">
                              <p className="text-[11px] font-bold text-[#E63946] flex items-center gap-1"><CornerDownRight className="h-3 w-3" /> Risposta del negozio</p>
                              <p className="text-sm text-slate-600">{r.reply}</p>
                            </div>
                          )}
                          {r.disputed && (
                            <div className="mt-2.5 rounded-xl bg-amber-50 p-2.5" data-testid={`my-review-dispute-${r.review_id}`}>
                              <p className="text-xs text-amber-800 mb-2">Il negozio ha chiesto una verifica di questa recensione. Confermi che è corretta?</p>
                              <div className="flex gap-2">
                                <button onClick={() => respondDispute(r.review_id, "keep")} data-testid={`dispute-confirm-${r.review_id}`} className="flex-1 h-9 rounded-lg bg-emerald-600 text-white text-xs font-bold">Confermo</button>
                                <button onClick={() => respondDispute(r.review_id, "remove")} data-testid={`dispute-delete-${r.review_id}`} className="flex-1 h-9 rounded-lg bg-white border border-slate-200 text-slate-600 text-xs font-bold">Rimuovi</button>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </MenuItem>

          {/* Invita e premi */}
          <MenuItem value="invite" icon={Gift} label="Invita e premi" hint={current ? `Livello ${current.label}` : "Invita amici e negozi"} testId="menu-invite">
            <div className="space-y-3">
              <div className="rounded-xl border border-slate-100 p-3 space-y-2.5" data-testid="invite-friends-card">
                <p className="font-bold text-slate-700 text-sm flex items-center gap-2"><Users className="h-4 w-4" /> Invita amici</p>
                <p className="text-sm text-slate-500">Condividi KoupoNow con i tuoi amici col tuo link personale.</p>
                <div className="flex gap-2">
                  <Button onClick={inviteFriendsWA} data-testid="invite-friends-wa" className="flex-1 h-11 rounded-xl font-bold bg-[#25D366] hover:bg-[#1eba57] gap-2">WhatsApp</Button>
                  <Button onClick={inviteFriendsEmail} variant="outline" data-testid="invite-friends-email" className="flex-1 h-11 rounded-xl font-bold gap-2"><Mail className="h-4 w-4" /> Email</Button>
                </div>
              </div>
              <div className="rounded-xl border border-slate-100 p-3" data-testid="invite-badge-card">
                <div className="flex items-center gap-3">
                  <div className={`h-14 w-14 rounded-2xl bg-gradient-to-br ${current ? current.color : "from-slate-200 to-slate-300"} flex items-center justify-center text-white shrink-0 shadow-sm`}>
                    <Award className="h-7 w-7" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-extrabold text-slate-900" data-testid="invite-count">Hai invitato {inviteCount} {inviteCount === 1 ? "negozio" : "negozi"}</p>
                      {current && <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-[#FFE4E6] text-[#BE123C]">{current.label}</span>}
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {next ? `Ancora ${toNext} per diventare ${next.label}` : (current ? "Sei al livello massimo, grazie! 🎉" : "Invita un negozio per sbloccare il badge Esploratore")}
                    </p>
                    {joinedCount > 0 && (
                      <p className="text-xs font-bold text-emerald-600 mt-1" data-testid="invite-joined-count">
                        🎉 {joinedCount} {joinedCount === 1 ? "negozio si è iscritto" : "negozi si sono iscritti"} grazie a te!
                      </p>
                    )}
                  </div>
                </div>
                <button onClick={() => navigate("/")} data-testid="invite-more-btn" className="mt-3 w-full h-10 rounded-xl bg-slate-900 text-white font-bold text-sm flex items-center justify-center gap-2 hover:bg-slate-800">
                  <UserPlus className="h-4 w-4" /> Invita altri negozi
                </button>
              </div>
            </div>
          </MenuItem>
        </Accordion>
      </div>

      <Button variant="outline" onClick={logout} data-testid="account-logout" className="w-full h-12 rounded-xl font-bold text-[#DC2626] border-[#FECACA] hover:bg-red-50 gap-2">
        <LogOut className="h-4 w-4" /> Esci
      </Button>

      <DeleteAccountSection role="client" />

      {/* Legal links: discreet, below delete account */}
      <div className="pt-1 pb-2 flex flex-col items-center gap-1.5" data-testid="account-legal-links">
        <div className="flex items-center justify-center gap-x-4 gap-y-1 flex-wrap">
          {[["termini", "Termini e Condizioni"], ["privacy", "Privacy Policy"], ["cookie", "Cookie Policy"]].map(([slug, label]) => (
            <button key={slug} onClick={() => navigate(`/legal/${slug}`)} data-testid={`account-legal-${slug}`}
              className="text-[11px] text-slate-400 hover:text-slate-600 underline underline-offset-2 transition-colors">
              {label}
            </button>
          ))}
        </div>
        {client?.consent?.accepted_at && (
          <p className="text-[10px] text-slate-300" data-testid="account-consent-proof">
            Consenso accettato il {new Date(client.consent.accepted_at).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" })} · Termini v{client.consent.terms_version} · Privacy v{client.consent.privacy_version}
          </p>
        )}
      </div>

      <AlertMeSheet open={alertOpen} onOpenChange={setAlertOpen} macros={macros} />
    </div>
  );
}
